import { forwardRef } from "react";
// @ts-expect-error - this will be resolved at build time
import { Text, isWeb, isNative } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  getTypographyTokens,
  getFonts,
  cssVar,
} from "@xsolla/xui-core";
import type { ColorType, TypographyProps, VariantType } from "./types";

const getColor = (
  color: ColorType | string,
  theme: any
): string | undefined => {
  if (color === "inherit") {
    return isNative ? theme?.colors?.content?.primary : undefined;
  }

  const colorMap: Record<string, string> = {
    primary: theme?.colors?.content?.primary,
    secondary: theme?.colors?.content?.secondary,
    tertiary: theme?.colors?.content?.tertiary,
    brand: theme?.colors?.content?.brand?.primary,
    brandSecondary: theme?.colors?.content?.brand?.secondary,
    success: theme?.colors?.content?.success?.primary,
    warning: theme?.colors?.content?.warning?.primary,
    alert: theme?.colors?.content?.alert?.primary,
    neutral: theme?.colors?.content?.neutral?.primary,
  };

  return colorMap[color] || color;
};

const isHeadingVariant = (variant: VariantType): boolean =>
  variant === "h1" ||
  variant === "h2" ||
  variant === "h3" ||
  variant === "h4" ||
  variant === "h5" ||
  variant === "display";

// Paragraph variants (bodyLgParagraph, …) use the Figma `text` role font,
// which differs from the `body`/compact font for the corpsite context.
const isParagraphVariant = (variant: VariantType): boolean =>
  variant.endsWith("Paragraph");

const getDefaultElement = (variant: VariantType): string | undefined => {
  const elementMap: Record<string, string> = {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
  };
  return elementMap[variant];
};

const bodyMap: Record<string, string> = {
  bodyLg: "body-lg",
  bodyLgAccent: "body-lg",
  bodyLgParagraph: "body-lg",
  bodyMd: "body-md",
  bodyMdAccent: "body-md",
  bodyMdParagraph: "body-md",
  bodySm: "body-sm",
  bodySmAccent: "body-sm",
  bodySmParagraph: "body-sm",
  bodyXs: "body-xs",
  bodyXsAccent: "body-xs",
  bodyXsParagraph: "body-xs",
  bodyXxs: "body-xxs",
  bodyXxsAccent: "body-xxs",
  bodyXxsParagraph: "body-xxs",
};

const headingMap: Record<string, string> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
};

const getVariantStyles = (variant: VariantType, typographyTokens?: any) => {
  if (typographyTokens) {
    if (headingMap[variant]) {
      const token = typographyTokens.heading[headingMap[variant]];
      if (token) {
        return {
          fontSize: token.fontSize,
          fontWeight: String(token.fontWeight),
          lineHeight: token.lineHeight,
          scaleStep: token.scaleStep,
          lineHeightCategory: token.lineHeightCategory,
        };
      }
    }

    if (variant === "display" && typographyTokens.basic?.display) {
      const token = typographyTokens.basic.display;
      return {
        fontSize: token.fontSize,
        fontWeight: String(token.fontWeight),
        lineHeight: token.lineHeight,
        scaleStep: token.scaleStep,
        lineHeightCategory: token.lineHeightCategory,
      };
    }

    const bodyKey = bodyMap[variant];
    if (bodyKey && typographyTokens.basic?.[bodyKey]) {
      const token = typographyTokens.basic[bodyKey];
      let fontWeight = token.fontWeight;
      let lineHeight = token.lineHeight;
      let lineHeightCategory = token.lineHeightCategory;
      let lineHeightScaleStep: string | undefined;

      if (variant.includes("Accent") && token.accent) {
        fontWeight = token.accent.fontWeight;
      }

      if (variant.includes("Paragraph") && token.paragraph) {
        lineHeight = token.paragraph.lineHeight;
        lineHeightCategory = "text";
        lineHeightScaleStep = token.paragraph.lineHeightScaleStep;
      }

      return {
        fontSize: token.fontSize,
        fontWeight: String(fontWeight),
        lineHeight,
        scaleStep: token.scaleStep,
        lineHeightCategory,
        lineHeightScaleStep,
      };
    }
  }

  switch (variant) {
    case "h1":
      return {
        fontSize: 56,
        fontWeight: "400",
        lineHeight: "56px",
        scaleStep: "650",
        lineHeightCategory: "display" as const,
      };
    case "h2":
      return {
        fontSize: 48,
        fontWeight: "400",
        lineHeight: "48px",
        scaleStep: "550",
        lineHeightCategory: "display" as const,
      };
    case "h3":
      return {
        fontSize: 32,
        fontWeight: "400",
        lineHeight: "32px",
        scaleStep: "350",
        lineHeightCategory: "display" as const,
      };
    case "h4":
      return {
        fontSize: 24,
        fontWeight: "400",
        lineHeight: "24px",
        scaleStep: "250",
        lineHeightCategory: "display" as const,
      };
    case "h5":
      return {
        fontSize: 20,
        fontWeight: "600",
        lineHeight: "20px",
        scaleStep: "200",
        lineHeightCategory: "display" as const,
      };
    case "display":
      return {
        fontSize: 64,
        fontWeight: "400",
        lineHeight: "64px",
        scaleStep: "750",
        lineHeightCategory: "display" as const,
      };
    case "bodyLg":
      return {
        fontSize: 18,
        fontWeight: "400",
        lineHeight: "24px",
        scaleStep: "175",
        lineHeightCategory: "compact" as const,
      };
    case "bodyLgAccent":
      return {
        fontSize: 18,
        fontWeight: "500",
        lineHeight: "24px",
        scaleStep: "175",
        lineHeightCategory: "compact" as const,
      };
    case "bodyLgParagraph":
      return {
        fontSize: 18,
        fontWeight: "400",
        lineHeight: "26px",
        scaleStep: "175",
        lineHeightCategory: "text" as const,
      };
    case "bodyMd":
      return {
        fontSize: 16,
        fontWeight: "400",
        lineHeight: "20px",
        scaleStep: "150",
        lineHeightCategory: "compact" as const,
      };
    case "bodyMdAccent":
      return {
        fontSize: 16,
        fontWeight: "500",
        lineHeight: "20px",
        scaleStep: "150",
        lineHeightCategory: "compact" as const,
      };
    case "bodyMdParagraph":
      return {
        fontSize: 16,
        fontWeight: "400",
        lineHeight: "22px",
        scaleStep: "150",
        lineHeightCategory: "text" as const,
      };
    case "bodySm":
      return {
        fontSize: 14,
        fontWeight: "400",
        lineHeight: "18px",
        scaleStep: "125",
        lineHeightCategory: "compact" as const,
      };
    case "bodySmAccent":
      return {
        fontSize: 14,
        fontWeight: "500",
        lineHeight: "18px",
        scaleStep: "125",
        lineHeightCategory: "compact" as const,
      };
    case "bodySmParagraph":
      return {
        fontSize: 14,
        fontWeight: "400",
        lineHeight: "20px",
        scaleStep: "125",
        lineHeightCategory: "text" as const,
      };
    case "bodyXs":
      return {
        fontSize: 12,
        fontWeight: "400",
        lineHeight: "16px",
        scaleStep: "100",
        lineHeightCategory: "compact" as const,
      };
    case "bodyXsAccent":
      return {
        fontSize: 12,
        fontWeight: "500",
        lineHeight: "16px",
        scaleStep: "100",
        lineHeightCategory: "compact" as const,
      };
    case "bodyXsParagraph":
      return {
        fontSize: 12,
        fontWeight: "400",
        lineHeight: "18px",
        scaleStep: "100",
        lineHeightCategory: "text" as const,
      };
    case "bodyXxs":
      return {
        fontSize: 10,
        fontWeight: "400",
        lineHeight: "14px",
        scaleStep: "75",
        lineHeightCategory: "compact" as const,
      };
    case "bodyXxsAccent":
      return {
        fontSize: 10,
        fontWeight: "500",
        lineHeight: "14px",
        scaleStep: "75",
        lineHeightCategory: "compact" as const,
      };
    case "bodyXxsParagraph":
      return {
        fontSize: 10,
        fontWeight: "400",
        lineHeight: "14px",
        scaleStep: "75",
        lineHeightCategory: "text" as const,
      };
    default:
      return {
        fontSize: 16,
        fontWeight: "400",
        lineHeight: "20px",
        scaleStep: "150",
        lineHeightCategory: "compact" as const,
      };
  }
};

function getResponsiveLineHeight(
  styles: ReturnType<typeof getVariantStyles>
): string | undefined {
  const step = styles.lineHeightScaleStep || styles.scaleStep;
  if (!step) return undefined;

  const cat = styles.lineHeightCategory;
  if (cat === "display") return cssVar.lhDisplay(step);
  if (cat === "text") return cssVar.lhText(step);
  return cssVar.lhCompact(step);
}

export const Typography = forwardRef<any, TypographyProps>(
  (
    {
      children,
      align = "inherit",
      noWrap = false,
      variant = "bodyMd",
      color = "inherit",
      marginBottom = 0,
      marginTop = 0,
      as,
      productContext,
      testID,
      themeMode,
      themeProductContext,
      ...rest
    },
    ref
  ) => {
    const resolvedTheme = useResolvedTheme({ themeMode, themeProductContext });
    const { theme } = resolvedTheme;
    // `responsive` was added to DesignSystemContext; cast for installed-package type compat
    const responsive: boolean =
      (resolvedTheme as { responsive?: boolean }).responsive ?? true;
    const typographyTokens = productContext
      ? getTypographyTokens(productContext)
      : (theme as any).typographyTokens;
    const variantStyles = getVariantStyles(variant, typographyTokens);
    const colorValue = getColor(color, theme);
    const contextFonts = productContext
      ? getFonts(productContext)
      : (theme.fonts as Record<string, string>);
    const fontFamily = isHeadingVariant(variant)
      ? contextFonts?.heading
      : isParagraphVariant(variant)
        ? contextFonts?.text
        : contextFonts?.body;

    const useResponsiveCSS = isWeb && responsive && variantStyles.scaleStep;

    const responsiveFontSize = useResponsiveCSS
      ? cssVar.fontSize(variantStyles.scaleStep!)
      : undefined;
    const responsiveLineHeight = useResponsiveCSS
      ? getResponsiveLineHeight(variantStyles)
      : undefined;

    const resolvedAs = isWeb ? as || getDefaultElement(variant) : as;

    return (
      <Text
        {...rest}
        ref={ref}
        testID={testID}
        as={resolvedAs}
        color={colorValue}
        fontSize={useResponsiveCSS ? undefined : variantStyles.fontSize}
        fontWeight={variantStyles.fontWeight}
        fontFamily={fontFamily}
        numberOfLines={noWrap ? 1 : undefined}
        style={{
          marginTop,
          marginBottom,
          textAlign: align === "inherit" ? undefined : align,
          ...(useResponsiveCSS
            ? { fontSize: responsiveFontSize, lineHeight: responsiveLineHeight }
            : { lineHeight: variantStyles.lineHeight }),
          ...(noWrap && {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }),
          ...rest.style,
        }}
      >
        {children}
      </Text>
    );
  }
);

Typography.displayName = "Typography";
